import { createStyles } from 'antd-style'

import { DETAIL_WIDTH, STAGE_HEIGHT } from './constants'

export const useStyles = createStyles(({ token, css }) => {
  // Keyframes live in one class applied to the card root so they're emitted
  // while the card is mounted; referenced by name from node/edge/ticker styles.
  const keyframes = css`
    @keyframes cg-dash {
      to {
        stroke-dashoffset: -22;
      }
    }
    @keyframes cg-pulse {
      0%,
      100% {
        opacity: 0.18;
      }
      50% {
        opacity: 0.04;
      }
    }
    @keyframes cg-fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
    @keyframes cg-live {
      0%,
      100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.4;
        transform: scale(0.8);
      }
    }
    @keyframes cg-ripple {
      from {
        transform: scale(1);
        opacity: 0.6;
      }
      to {
        transform: scale(2.6);
        opacity: 0;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .cg-flow,
      .cg-pulse,
      .cg-live-dot {
        animation: none !important;
      }
    }
  `

  return {
    keyframes,

    // Top-level vertical stack for the whole page view. Also the host element
    // for the @keyframes class so the animations are emitted while mounted.
    view: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16
    },

    // Header card holding the KPI strip + the "needs attention" lane. Both inner
    // strips carry a bottom divider; the last one would draw a redundant trailing
    // line against the card edge, so strip it.
    headerCard: {
      '.ant-card-body > *:last-child': {
        borderBottom: 'none'
      }
    },

    // Equal-height two-column row (graph card | AI insights card).
    columns: {
      alignItems: 'stretch'
    },

    graphCard: {
      height: '100%',
      '.ant-card-body': {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }
    },

    // Fills the graph card below the tab bar: legend strip + canvas stage.
    graphTabPanel: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0
    },

    aiCard: {
      height: '100%',
      '.ant-card-body': {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }
    },

    aiHead: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 7,
      padding: '11px 14px',
      borderBottom: `1px solid ${token.colorBorderSecondary}`,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: token.colorTextSecondary
    },

    legendBar: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap',
      padding: '10px 14px',
      borderBottom: `1px solid ${token.colorBorderSecondary}`,
      fontSize: 11,
      color: token.colorTextSecondary
    },

    legendItem: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5
    },

    legendSwatch: {
      width: 9,
      height: 9,
      borderRadius: 3,
      flex: '0 0 auto'
    },

    kpiRow: {
      display: 'flex',
      alignItems: 'stretch',
      borderBottom: `1px solid ${token.colorBorderSecondary}`,
      flexWrap: 'wrap'
    },

    kpiTile: {
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
      padding: '8px 14px',
      flex: 1,
      minWidth: 110
    },

    kpiLabel: {
      fontSize: 10,
      color: token.colorTextSecondary,
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap'
    },

    kpiValue: {
      margin: 0,
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.1,
      fontVariantNumeric: 'tabular-nums'
    },

    kpiPeriod: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 14px',
      flex: '0 0 auto'
    },

    stage: {
      position: 'relative',
      height: STAGE_HEIGHT,
      overflow: 'hidden'
    },

    stageLoading: {
      padding: 20
    },

    svg: {
      width: '100%',
      height: '100%',
      display: 'block',
      cursor: 'grab',
      touchAction: 'none',
      '&:active': {
        cursor: 'grabbing'
      }
    },

    hint: {
      position: 'absolute',
      bottom: 10,
      left: 14,
      fontSize: 11,
      color: token.colorTextQuaternary,
      pointerEvents: 'none'
    },

    zoomControls: {
      position: 'absolute',
      right: 12,
      bottom: 12,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      zIndex: 2
    },

    edgeHead: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',
      paddingRight: 20
    },

    edgeEndpoint: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      fontSize: 13,
      fontWeight: 600,
      minWidth: 0
    },

    // Floating card overlaying the canvas (not a page Drawer). Slides in from
    // the stage's right edge; the graph stays visible/interactive beside it.
    // Floating card overlaying the canvas's right edge. Mounted only while open
    // (rendered conditionally) — no transform/transition slide, which fought the
    // graph's constant re-renders and left the panel stuck closed.
    detail: {
      position: 'absolute',
      top: 12,
      right: 12,
      bottom: 12,
      width: DETAIL_WIDTH,
      maxWidth: 'calc(100% - 24px)',
      background: token.colorBgElevated,
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: token.borderRadiusLG,
      boxShadow: token.boxShadowSecondary,
      padding: 16,
      overflow: 'auto',
      zIndex: 3
    },

    stageWrap: {
      position: 'relative',
      overflow: 'hidden'
    },

    detailClose: {
      position: 'absolute',
      top: 12,
      right: 12,
      cursor: 'pointer',
      border: 'none',
      background: 'none',
      color: token.colorTextSecondary,
      fontSize: 18,
      lineHeight: 1,
      padding: 0
    },

    detailHead: {
      display: 'flex',
      alignItems: 'center',
      gap: 9,
      paddingRight: 20
    },

    detailDot: {
      width: 11,
      height: 11,
      borderRadius: 4,
      flex: '0 0 auto'
    },

    detailName: {
      margin: 0,
      fontSize: 14,
      fontWeight: 600,
      minWidth: 0
    },

    statusPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 12,
      fontWeight: 500,
      color: token.colorSuccess,
      background: token.colorSuccessBg,
      border: `1px solid ${token.colorSuccessBorder}`,
      borderRadius: 20,
      padding: '2px 10px'
    },

    statusDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: token.colorSuccess,
      boxShadow: `0 0 6px ${token.colorSuccess}`
    },

    detailSection: {
      marginTop: 14,
      borderTop: `1px solid ${token.colorBorderSecondary}`,
      paddingTop: 12
    },

    sectionLabel: {
      fontSize: 10,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      color: token.colorTextTertiary,
      marginBottom: 9
    },

    factRow: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 12,
      fontSize: 12,
      padding: '5px 0'
    },

    factLabel: {
      color: token.colorTextSecondary
    },

    factValue: {
      color: token.colorText,
      textAlign: 'right'
    },

    timeline: {
      display: 'flex',
      flexDirection: 'column',
      gap: 11
    },

    timelineRow: {
      display: 'flex',
      gap: 9,
      alignItems: 'flex-start'
    },

    timelineDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      marginTop: 4,
      flex: '0 0 auto'
    },

    timelineText: {
      fontSize: 12,
      lineHeight: 1.4
    },

    timelineWhen: {
      fontSize: 10.5,
      color: token.colorTextTertiary,
      marginTop: 1
    },

    emptyText: {
      fontSize: 12,
      color: token.colorTextTertiary
    },

    connChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 11,
      background: token.colorFillTertiary,
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: 7,
      padding: '4px 8px',
      margin: '0 6px 6px 0',
      cursor: 'pointer',
      transition: `border-color ${token.motionDurationFast}`,
      '&:hover': {
        borderColor: token.colorPrimary
      }
    },

    connSwatch: {
      width: 8,
      height: 8,
      borderRadius: 3,
      flex: '0 0 auto'
    },

    // Floating live-activity card, docked left inside the canvas, always visible.
    liveCard: {
      position: 'absolute',
      top: 12,
      left: 12,
      width: 236,
      maxWidth: 'calc(100% - 24px)',
      maxHeight: 'calc(100% - 24px)',
      display: 'flex',
      flexDirection: 'column',
      background: token.colorBgElevated,
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: token.borderRadiusLG,
      boxShadow: token.boxShadowSecondary,
      overflow: 'hidden',
      zIndex: 2
    },

    liveList: {
      flex: 1,
      overflow: 'hidden',
      padding: '2px 0'
    },

    liveRow: {
      display: 'flex',
      gap: 8,
      alignItems: 'flex-start',
      padding: '7px 12px',
      animation: `cg-fade-in ${token.motionDurationMid} ${token.motionEaseOut}`,
      '& + &': {
        borderTop: `1px solid ${token.colorBorderSecondary}`
      }
    },

    liveRowMain: {
      flex: 1,
      minWidth: 0
    },

    liveRowText: {
      display: 'block',
      fontSize: 11.5,
      color: token.colorTextSecondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },

    tickerHead: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      padding: '9px 12px 7px',
      fontSize: 11,
      fontWeight: 600,
      color: token.colorTextSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      borderBottom: `1px solid ${token.colorBorderSecondary}`
    },

    tickerLiveDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: token.colorSuccess,
      boxShadow: `0 0 6px ${token.colorSuccess}`,
      animation: 'cg-live 1.6s ease-in-out infinite'
    },

    tickerDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      flex: '0 0 auto'
    },

    tickerNode: {
      fontSize: 12.5,
      fontWeight: 600,
      cursor: 'pointer',
      flex: '1 1 auto',
      minWidth: 0,
      '&:hover': {
        color: token.colorPrimary
      }
    },

    sampleTag: {
      marginInlineStart: 0,
      marginInlineEnd: 0
    },

    feedFilters: {
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap',
      padding: '10px 14px',
      borderBottom: `1px solid ${token.colorBorderSecondary}`
    },

    feedList: {
      maxHeight: 280,
      overflow: 'auto'
    },

    feedRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 11,
      padding: '9px 14px',
      '& + &': {
        borderTop: `1px solid ${token.colorBorderSecondary}`
      }
    },

    feedIcon: {
      width: 28,
      height: 28,
      borderRadius: 7,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 auto'
    },

    feedActor: {
      fontWeight: 600,
      fontSize: 13
    },

    feedAction: {
      fontSize: 13,
      color: token.colorTextSecondary
    },

    feedDetail: {
      fontSize: 11,
      marginInlineEnd: 0
    },

    feedEmpty: {
      padding: '26px 14px',
      textAlign: 'center'
    },

    insights: {
      flex: 1,
      minHeight: 0,
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 12
    },

    insightSummary: {
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap'
    },

    insightStat: {
      flex: 1,
      minWidth: 92,
      padding: '8px 10px',
      borderRadius: token.borderRadius,
      border: `1px solid ${token.colorBorderSecondary}`,
      background: token.colorFillQuaternary
    },

    insightStatValue: {
      margin: 0,
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.1,
      fontVariantNumeric: 'tabular-nums'
    },

    insightStatLabel: {
      fontSize: 10,
      color: token.colorTextSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    },

    chatThread: {
      flex: 1,
      minHeight: 140,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      overflow: 'auto',
      padding: '4px 2px'
    },

    chatBubble: {
      maxWidth: '88%',
      padding: '7px 11px',
      borderRadius: token.borderRadiusLG,
      fontSize: 12.5,
      lineHeight: 1.45,
      whiteSpace: 'pre-wrap'
    },

    chatUser: {
      alignSelf: 'flex-end',
      background: token.colorPrimary,
      color: token.colorTextLightSolid,
      borderBottomRightRadius: 2
    },

    chatAssistant: {
      alignSelf: 'flex-start',
      background: token.colorFillSecondary,
      color: token.colorText,
      borderBottomLeftRadius: 2
    },

    chatPrompts: {
      display: 'flex',
      gap: 6,
      flexWrap: 'wrap'
    },

    promptChip: {
      fontSize: 11,
      padding: '3px 9px',
      borderRadius: 14,
      border: `1px solid ${token.colorBorderSecondary}`,
      background: token.colorBgContainer,
      color: token.colorTextSecondary,
      cursor: 'pointer',
      transition: `border-color ${token.motionDurationFast}, color ${token.motionDurationFast}`,
      '&:hover': {
        borderColor: token.colorPrimary,
        color: token.colorPrimary
      }
    },

    attentionLane: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 14px',
      borderBottom: `1px solid ${token.colorBorderSecondary}`,
      overflowX: 'auto'
    },

    attentionHead: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: token.colorTextSecondary,
      flex: '0 0 auto'
    },

    attentionCount: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 16,
      height: 16,
      padding: '0 4px',
      borderRadius: 8,
      fontSize: 10,
      fontWeight: 600,
      background: token.colorErrorBg,
      color: token.colorError
    },

    attentionItem: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      flex: '0 0 auto',
      padding: '5px 10px',
      borderRadius: token.borderRadius,
      border: `1px solid ${token.colorBorderSecondary}`,
      background: token.colorBgContainer,
      cursor: 'pointer',
      transition: `border-color ${token.motionDurationFast}, background ${token.motionDurationFast}`,
      '&:hover': {
        borderColor: token.colorPrimary,
        background: token.colorFillQuaternary
      }
    },

    attentionIcon: {
      display: 'inline-flex',
      flex: '0 0 auto'
    },

    attentionText: {
      display: 'flex',
      flexDirection: 'column',
      lineHeight: 1.25
    },

    attentionTitle: {
      fontSize: 12,
      fontWeight: 600
    },

    attentionMeta: {
      fontSize: 10.5,
      color: token.colorTextSecondary,
      whiteSpace: 'nowrap'
    },

    allClear: {
      fontSize: 12,
      color: token.colorTextSecondary
    },

    kpiDelta: {
      fontSize: 11,
      fontWeight: 500,
      fontVariantNumeric: 'tabular-nums'
    },

    digest: {
      display: 'flex',
      flexDirection: 'column',
      gap: 7
    },

    digestItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      fontSize: 12.5,
      lineHeight: 1.4
    },

    digestIcon: {
      flex: '0 0 auto',
      marginTop: 1
    },

    digestCite: {
      fontSize: 11,
      marginInlineStart: 4
    }
  }
})
